import { useRef, useState } from 'react'
import DataGrid, {
  Column,
  DataGridRef,
  Editing,
  Export,
  Item,
  StateStoring,
  Toolbar,
  Selection,
  Paging,
} from 'devextreme-react/data-grid'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ordine, task } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useScreenSize } from '@/themes/media-query'
import { ClickEvent } from 'devextreme/ui/button'
import CustomStore from 'devextreme/data/custom_store'
import saveAs from 'file-saver'
import dxDataGrid, { ColumnButtonClickEvent, ExportingEvent, RowClickEvent } from 'devextreme/ui/data_grid'
import { Button as GridButton } from 'devextreme-react/cjs/data-grid'
import { AxiosResponse } from 'axios'
import notify from 'devextreme/ui/notify'
import OutlookAppointmentSelector from '@/routes/tasks/OutlookAppointmentSelector'
import { PopupRef } from 'devextreme-react/cjs/popup'
import { StatoOrdine } from '@/routes/orders/order.types'
import { confirm } from 'devextreme/ui/dialog'

interface TaskAppointment {
  id: string
  task: string
  order: string
  user: string
  Location: string
  Subject: string
  StartDate: string
  EndDate: string
  Duration: number
  ShowAs: number
  Notes: string
  Remote: boolean
  Provisional: boolean
  WebLink: string
  User: { id: number; fullName: string; email: string }
  CreatedBy?: string
  CreatedAt?: string
  ModifiedBy?: string
  ModifiedAt?: string
}

export interface TaskPlannerProps {
  task?: task
  order?: ordine
  mode?: 'readonly' | 'browse' | 'manage'
}

enum eventState {
  Sconosciuto = 0,
  Disponibile = 1,
  Provvisorio = 2,
  Occupato = 3,
  Oof = 4,
  Altro = 5,
}

export const TaskPlanner = (props: TaskPlannerProps) => {
  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const client = useQsAdminApiManager.getState().client
  const createOutlookApponitmentPopupRef = useRef<PopupRef>(null)
  const [appointmentData, setAppointmentData] = useState<any>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const mode = props.mode ?? 'browse'
  const isReadOnly = mode === 'readonly'
  const canManage = mode === 'manage'
  const canManageTaskAppointments = canManage && props.task !== undefined
  // La conferma agisce sui singoli appuntamenti (id + email utente), quindi è disponibile anche
  // quando la griglia mostra le pianificazioni di un ordine
  const canConfirmAppointments = canManage

  const store = new CustomStore({
    key: 'id',
    async load(loadOptions) {
      try {
        let planningUrl: string
        if (props.task !== undefined) {
          planningUrl = `/api/calendar/task/load?taskId=${props.task.id}`
        } else if (props.order !== undefined) {
          planningUrl = `/api/calendar/task/load?orderId=${props.order.id}`
        } else {
          throw new Error(`Dati necessari del task o dell'ordine mancanti`)
        }

        const response = await client.get(planningUrl, {
          headers: {
            'Content-Type': 'application/json',
          },
        })

        const result: TaskAppointment[] = response.data

        return {
          data: result,
          totalCount: result.length,
        }
      } catch (error) {
        throw new Error(`Data Loading Error : ${error}`)
      }
    },
  })

  const onExporting = (e: ExportingEvent) => {
    ExportDataGridToExcel(e.component)
  }

  const ExportDataGridToExcel = (component: dxDataGrid) => {
    let planningExportUrl: string
    let planningExportFile: string
    if (props.task !== undefined) {
      planningExportUrl = `/api/calendar/task/export-planning?taskId=${props.task.id}`
      planningExportFile = `Planning-Task-${props.task?.ded_Dis}.xlsx`
    } else if (props.order !== undefined) {
      planningExportUrl = `/api/calendar/task/export-planning?orderId=${props.order.id}`
      planningExportFile = `Planning-Order-${props.order?.ded_Dis}.xlsx`
    } else {
      throw new Error(`Dati necessari del task o dell'ordine mancanti`)
    }

    try {
      client
        .post(planningExportUrl, null, {
          headers: {
            'Content-Disposition': `attachment;filename=${planningExportFile}`,
            'Content-Type': 'application/octet-stream',
          },
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          saveAs(new Blob([response.data], { type: 'application/octet-stream' }), planningExportFile)
        })
    } catch (error: unknown) {
      notify(`Errore esportazione task planning. Dettagli : ${error}`, 'error', 5000)
    }
  }

  // Con le checkbox sempre visibili DevExtreme ignora il click sulla riga ai fini della selezione: lo
  // gestiamo qui, così cliccare un punto qualsiasi della riga equivale a spuntare/togliere la sua checkbox.
  // Il click sulla cella della checkbox non arriva a questo handler: lo gestisce già DevExtreme.
  const onRowClick = (e: RowClickEvent) => {
    if (e.rowType !== 'data') return
    // Il click sui pulsanti di riga (es. apertura evento in Outlook) non deve toccare la selezione
    if ((e.event?.target as HTMLElement | undefined)?.closest('.dx-command-edit')) return

    if (e.component.isRowSelected(e.key)) e.component.deselectRows([e.key])
    else e.component.selectRows([e.key], true)
  }

  // Riga su cui agiscono le azioni su singolo elemento: valida solo quando è spuntata una sola riga
  const getSingleSelectedAppointment = (): TaskAppointment | undefined => {
    const selectedRows = (gridRef.current?.instance().getSelectedRowsData() ?? []) as TaskAppointment[]
    return selectedRows.length === 1 ? selectedRows[0] : undefined
  }

  const onAddOutlookAppointmentForTaskClick = (e: ClickEvent) => {
    if (
      props.task?.ordine?.stato?.nome === StatoOrdine.DA_EVADERE ||
      props.task?.ordine?.stato?.nome === StatoOrdine.EVASO ||
      !props.task?.ordine
    ) {
      setIsEditing(false)
      setAppointmentData(undefined)
      createOutlookApponitmentPopupRef.current?.instance().show()
    } else
      notify(
        `L'ordine deve essere nello stato 'DA EVADERE' o 'EVASO'. Stato ordine: '${props.task?.ordine?.stato?.nome}'`,
        'warning',
        3000,
      )
  }

  const onDuplicateOutlookAppointmentForTaskClick = (data: any) => {
    if (
      props.task?.ordine?.stato?.nome === StatoOrdine.DA_EVADERE ||
      props.task?.ordine?.stato?.nome === StatoOrdine.EVASO ||
      !props.task?.ordine
    ) {
      setAppointmentData(data)
      setIsEditing(false)
      createOutlookApponitmentPopupRef.current?.instance().show()
    } else
      notify(
        `L'ordine deve essere nello stato 'DA EVADERE' o 'EVASO'. Stato ordine: '${props.task?.ordine?.stato?.nome}'`,
        'warning',
        3000,
      )
  }

  const onEditOutlookAppointmentForTaskClick = (data: any) => {
    if (
      props.task?.ordine?.stato?.nome === StatoOrdine.DA_EVADERE ||
      props.task?.ordine?.stato?.nome === StatoOrdine.EVASO ||
      !props.task?.ordine
    ) {
      setAppointmentData(data)
      setIsEditing(true)
      createOutlookApponitmentPopupRef.current?.instance().show()
    } else
      notify(
        `L'ordine deve essere nello stato 'DA EVADERE' o 'EVASO'. Stato ordine: '${props.task?.ordine?.stato?.nome}'`,
        'warning',
        3000,
      )
  }

  const deleteOutlookAppointment = async (data: TaskAppointment) => {
    if (!data?.User?.email || !data?.id) {
      notify("Dati appuntamento insufficienti per procedere con l'eliminazione.", 'warning', 2500)
      return
    }

    const dialogResult = await confirm(
      `<i>Vuoi davvero eliminare la pianificazione <b>${data.Subject ?? data.task ?? data.id}</b>?</i>`,
      'Eliminazione pianificazione',
    )
    if (!dialogResult) return

    gridRef.current?.instance().beginCustomLoading('Eliminazione pianificazione in corso...')

    try {
      await client.delete(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/delete`, {
        data: {
          email: data.User.email,
          appointmentId: data.id,
        },
      })
      notify('Pianificazione eliminata con successo.', 'success', 2500)
      gridRef.current?.instance().refresh()
    } catch (error) {
      notify(`Errore eliminazione pianificazione. Dettagli : ${error}`, 'error', 4000)
    } finally {
      gridRef.current?.instance().endCustomLoading()
    }
  }

  const onDeleteOutlookAppointmentForTaskClick = () => {
    const appointment = getSingleSelectedAppointment()
    if (!appointment) {
      notify('Selezionare una sola pianificazione per poterla eliminare.', 'warning', 2000)
      return
    }

    deleteOutlookAppointment(appointment)
  }

  // Conferma in blocco: porta le pianificazioni selezionate da provvisorio (Tentative) a confermato (Busy).
  // L'endpoint dedicato modifica solo la disponibilità, lasciando invariati oggetto, note, luogo e date.
  const onConfirmSelectedAppointmentsClick = async () => {
    const selectedRows = (gridRef.current?.instance().getSelectedRowsData() ?? []) as TaskAppointment[]
    if (selectedRows.length === 0) {
      notify('Selezionare almeno una pianificazione da confermare.', 'warning', 2000)
      return
    }

    const provisionalRows = selectedRows.filter((row) => row.Provisional && row.id && row.User?.email)
    if (provisionalRows.length === 0) {
      notify('Le pianificazioni selezionate sono già confermate.', 'warning', 2500)
      return
    }

    const skippedCount = selectedRows.length - provisionalRows.length
    const messageHtml =
      provisionalRows.length === 1
        ? `<i>Vuoi confermare la pianificazione <b>${provisionalRows[0].Subject ?? provisionalRows[0].id}</b>?</i>`
        : `<i>Vuoi confermare <b>${provisionalRows.length}</b> pianificazioni provvisorie?</i>`
    let skippedHtml = ''
    if (skippedCount === 1) {
      skippedHtml = '<br/><br/>1 pianificazione già confermata verrà ignorata.'
    } else if (skippedCount > 1) {
      skippedHtml = `<br/><br/>${skippedCount} pianificazioni già confermate verranno ignorate.`
    }

    const dialogResult = await confirm(`${messageHtml}${skippedHtml}`, 'Conferma pianificazioni')
    if (!dialogResult) return

    gridRef.current?.instance().beginCustomLoading('Conferma pianificazioni in corso...')

    try {
      const response = await client.put(
        '/api/calendar/task/confirm',
        {
          appointments: provisionalRows.map((row) => ({ id: row.id, userEmail: row.User.email })),
        },
        { headers: { 'Content-Type': 'application/json' } },
      )
      const confirmedCount: number = response.data?.confirmed ?? 0
      const failures: { id: string; error: string }[] = response.data?.failed ?? []
      if (failures.length > 0) {
        notify(
          `Confermate ${confirmedCount} pianificazioni su ${provisionalRows.length}. ${failures.length} non aggiornate.`,
          'warning',
          4000,
        )
      } else {
        const successMessage =
          confirmedCount === 1
            ? 'Pianificazione confermata con successo.'
            : `Confermate ${confirmedCount} pianificazioni.`
        notify(successMessage, 'success', 2500)
      }
      gridRef.current?.instance().clearSelection()
      gridRef.current?.instance().refresh()
    } catch (error) {
      notify(`Errore conferma pianificazioni. Dettagli : ${error}`, 'error', 4000)
    } finally {
      gridRef.current?.instance().endCustomLoading()
    }
  }

  return (
    <div>
      <DataGrid
        dataSource={store}
        ref={gridRef}
        keyExpr="id"
        showBorders={true}
        showColumnLines={true}
        columnAutoWidth={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        onExporting={onExporting}
        onRowClick={onRowClick}
        noDataText="Nessun evento associato al task"
      >
        <StateStoring enabled={true} type="localStorage" storageKey={'task-planner-datagrid'} savingTimeout={50} />
        <Export enabled={!isReadOnly} />
        <Editing allowAdding={!isReadOnly} />
        {/* Checkbox sempre visibili: il click sulla riga le spunta/toglie (vedi onRowClick), la testata
            seleziona tutto. Le azioni su singolo elemento richiedono una sola riga spuntata. */}
        <Selection
          mode={'multiple'}
          showCheckBoxesMode="always"
          selectAllMode="allPages"
          allowSelectAll={true}
        ></Selection>
        <Paging pageSize={8}></Paging>
        {!isReadOnly && (
          <Toolbar>
            {!currentScreenSize.isXSmall && <Item name="groupPanel" />}
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Refresh',
                text: 'Refresh',
                icon: 'refresh',
                onClick: (e: ClickEvent) => {
                  gridRef.current?.instance().refresh()
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={canManageTaskAppointments}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              name="addRowButton"
              options={{
                onClick: onAddOutlookAppointmentForTaskClick,
                hint: 'Nuovo appuntamento outlook',
                text: 'Nuovo appuntamento outlook',
              }}
            />
            <Item
              visible={canManageTaskAppointments}
              location="after"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Modifica appuntamento outlook',
                text: 'Modifica appuntamento outlook',
                icon: 'edit',
                onClick: (e: ClickEvent) => {
                  const appointment = getSingleSelectedAppointment()
                  if (!appointment) notify('Selezionare un solo appuntamento per poterlo modificare.', 'warning', 2000)
                  else onEditOutlookAppointmentForTaskClick(appointment)
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={canManageTaskAppointments}
              location="after"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Duplica appuntamento outlook',
                text: 'Duplica appuntamento outlook',
                icon: 'copy',
                onClick: (e: ClickEvent) => {
                  const appointment = getSingleSelectedAppointment()
                  if (!appointment) notify('Selezionare un solo appuntamento per poterlo duplicare.', 'warning', 2000)
                  else onDuplicateOutlookAppointmentForTaskClick(appointment)
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={canConfirmAppointments}
              location="after"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Conferma le pianificazioni provvisorie selezionate',
                text: 'Conferma selezionati',
                icon: 'check',
                onClick: onConfirmSelectedAppointmentsClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={canManageTaskAppointments}
              location="after"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Elimina pianificazione outlook',
                text: 'Elimina pianificazione outlook',
                icon: 'trash',
                onClick: onDeleteOutlookAppointmentForTaskClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              name="exportButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
            />
          </Toolbar>
        )}
        <Column
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
        >
          <GridButton
            hint="Details"
            icon="search"
            onClick={(e: ColumnButtonClickEvent) => {
              window.open(e.row?.data.WebLink, '_blank')
            }}
          />
        </Column>
        <Column
          dataField="task"
          caption="TASK"
          visible={props.order !== null}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
          }
        />
        <Column
          dataField="order"
          caption="ORDINE"
          visible={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        />
        <Column
          dataField="User.fullName"
          caption="UTENTE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
          }
        />
        <Column
          dataField="Subject"
          caption="OGGETTO"
          visible={true}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
        />
        <Column
          dataField="Location"
          caption="LUOGO"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        />
        <Column
          dataField="StartDate"
          caption="INIZIO"
          dataType="date"
          format="dd-MM-yyyy HH:mm"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        />
        <Column
          dataField="EndDate"
          caption="FINE"
          dataType="date"
          format="dd-MM-yyyy HH:mm"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        />
        <Column
          dataField="Notes"
          caption="NOTE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        />
        <Column
          dataField="Duration"
          caption="DURATA"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        />
        <Column
          dataField="ShowAs"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
          caption="STATO"
          cellRender={({ data }) => {
            const value: number = data.ShowAs ?? 0
            return eventState[value]
          }}
        />
      </DataGrid>
      <OutlookAppointmentSelector
        task={props.task}
        appointmentData={appointmentData}
        appointmentGridRef={gridRef}
        popupRef={createOutlookApponitmentPopupRef}
        isEditing={isEditing}
      />
    </div>
  )
}
